import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  runTransaction,
  setDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const MemoryMatrix = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game settings
  const [gameMode, setGameMode] = useState("intro"); // intro, tutorial, play, results
  const [level, setLevel] = useState(1); // 1-back, 2-back, 3-back
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [currentSequence, setCurrentSequence] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Game metrics
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);
  const [misses, setMisses] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);

  // Visual settings
  const matrixSize = 3; // 3x3 grid
  const numSquares = matrixSize * matrixSize;
  const gridRef = useRef(null);
  const [activeSquare, setActiveSquare] = useState(null);
  const lastInteractionTime = useRef(Date.now());

  // Timer settings
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const isPlayingRef = useRef(false);

  // Colors based on working memory category
  const primaryColor = "from-blue-500 to-cyan-600";
  const primaryColorSolid = "bg-blue-500";
  const secondaryColor = "text-blue-300";
  const tertiaryColor = "bg-blue-500/10";

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialShowPattern, setTutorialShowPattern] = useState(false);
  const [tutorialPatternHistory, setTutorialPatternHistory] = useState([]);
  const [gameCompleted, setGameCompleted] = useState(false);

  // Game difficulty settings
  const baseSpeed = 2000; // ms between patterns
  const basePatternVisibility = 1200; // ms that a pattern is visible
  const patternCount = 30; // number of patterns in a game

  const [gameHistory, setGameHistory] = useState([]);

  // Fetch high scores when component mounts
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;

      try {
        // Reference the single leaderboard document for the "nback" game
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "nback"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Get the scores for the current level (as a string key)
          const scores =
            data.leaderboards && data.leaderboards[level.toString()]
              ? data.leaderboards[level.toString()]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, level]);

  // Start timer when game starts
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Generate a random sequence of patterns
  const generateSequence = useCallback(() => {
    const sequence = [];

    // Create some intentional matches based on the level (n-back)
    // This ensures a certain percentage of patterns will match the n-back pattern
    // making the game more balanced and engaging
    const matchChance = 0.3; // 30% chance of a match

    for (let i = 0; i < patternCount; i++) {
      // For the first n positions, just generate random patterns
      if (i < level) {
        sequence.push(Math.floor(Math.random() * numSquares));
        continue;
      }

      // After that, decide if this should be a match with the n-back position
      const shouldMatch = Math.random() < matchChance;

      if (shouldMatch) {
        // Create a match with the n-back position
        sequence.push(sequence[i - level]);
      } else {
        // Create a non-match, ensuring it doesn't accidentally match
        let newPattern;
        do {
          newPattern = Math.floor(Math.random() * numSquares);
        } while (newPattern === sequence[i - level]);

        sequence.push(newPattern);
      }
    }

    return sequence;
  }, [level, numSquares]);

  // Start a new game
  const startGame = () => {
    console.log("Starting game...");

    // Reset all game state
    setScore(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setMisses(0);
    setReactionTimes([]);

    // Create a fresh array with null values (representing "no response yet")
    setGameHistory(Array(patternCount).fill(null));

    setCurrentPosition(0);
    setTimeLeft(180);

    // Set both the state and the ref
    setIsPlaying(true);
    isPlayingRef.current = true;

    setGameStartTime(Date.now());
    setGameEndTime(null);

    const sequence = generateSequence();
    console.log("Generated sequence:", sequence);
    setCurrentSequence(sequence);

    // Start with a slight delay to ensure state updates
    setTimeout(() => {
      console.log("Starting pattern sequence...");
      showNextPattern(sequence, 0);
    }, 100);
  };

  const endGame = () => {
    console.log("Ending game...");

    // Clear any pending timeouts and intervals
    clearTimeout(); // Clear any pending timeouts
    clearInterval(timerRef.current);

    // Set game state to not playing
    setIsPlaying(false);
    isPlayingRef.current = false;

    // Record end time
    setGameEndTime(Date.now());

    // Change game mode to results
    setGameMode("results");
    setGameCompleted(currentPosition >= patternCount || timeLeft <= 1);

    // Remove the automatic score saving
    // saveScore();
  };

  // Save score to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "nback",
        gameId: "nback",
        mode: "single",
        userId: user.email,
        userName,
        score,
        level, // Instead of difficulty
        correctResponses,
        incorrectResponses,
        misses,
        averageReactionTime:
          reactionTimes.length > 0
            ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
            : 0,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "nback"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Sorting logic: score > speed > fewer total errors (incorrects + misses)
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.averageReactionTime !== b.averageReactionTime)
          return a.averageReactionTime - b.averageReactionTime;

        const aErrors = (a.incorrectResponses || 0) + (a.misses || 0);
        const bErrors = (b.incorrectResponses || 0) + (b.misses || 0);
        return aErrors - bErrors;
      };

      // 🔁 Transaction to update leaderboard + user
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = level.toString(); // N-back level acts like difficulty
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset UI
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // Show the next pattern in the sequence
  const showNextPattern = (sequence, position) => {
    console.log(`Showing pattern at position ${position}`);

    // FIX: Add a strict equality check here
    if (position >= sequence.length) {
      console.log("End of sequence reached, ending game");
      endGame(); // Make sure this function is being called
      return; // Make sure this return is working
    }

    // Show the pattern
    setActiveSquare(sequence[position]);
    lastInteractionTime.current = Date.now();

    // Hide the pattern after a delay
    setTimeout(() => {
      setActiveSquare(null);

      // Move to the next pattern after a delay
      setTimeout(() => {
        // Check if still playing using the ref instead of state
        if (isPlayingRef.current) {
          // Process response for patterns after the first n
          if (position >= level) {
            // Get current history state to avoid race conditions
            const currentHistory = [...gameHistory];
            const shouldHaveMatched =
              sequence[position] === sequence[position - level];
            const didRespond =
              currentHistory[position] !== null &&
              currentHistory[position] !== undefined;

            console.log(`Checking position ${position}:`);
            console.log(`  Should have matched: ${shouldHaveMatched}`);
            console.log(`  Did user respond: ${didRespond}`);

            // Only count as a miss after giving time for user input to register
            if (shouldHaveMatched && !didRespond) {
              setTimeout(() => {
                // One final check to make sure user input wasn't registered
                setGameHistory((prevHistory) => {
                  // Only count as miss if still no response
                  if (prevHistory[position] === null) {
                    console.log(`  Counted as a miss!`);
                    setMisses((prev) => prev + 1);
                    const newHistory = [...prevHistory];
                    newHistory[position] = false;
                    return newHistory;
                  }
                  return prevHistory;
                });
              }, 50); // Small delay to ensure handleUserInput completes
            }
          }

          // Update position
          setCurrentPosition(position + 1);

          // CRITICAL FIX: Add an additional check here to force game end if we're at the end
          if (position + 1 >= sequence.length) {
            console.log("Reached end of sequence, ending game");
            endGame();
            return;
          }

          // Call for next pattern
          showNextPattern(sequence, position + 1);
        }
      }, baseSpeed - basePatternVisibility);
    }, basePatternVisibility);
  };

  const handleUserInput = (isMatch) => {
    // Only process input during the game
    if (!isPlayingRef.current || currentPosition < level) return;

    console.log(
      `User input at position ${currentPosition}: ${
        isMatch ? "Match" : "No Match"
      }`
    );

    // Calculate reaction time
    const reactionTime = Date.now() - lastInteractionTime.current;
    setReactionTimes((prev) => [...prev, reactionTime]);

    // Check if the answer is correct
    const currentPattern = currentSequence[currentPosition];
    const nBackPattern = currentSequence[currentPosition - level];
    const isCorrectMatch = currentPattern === nBackPattern;

    console.log(
      `  Current pattern: ${currentPattern}, N-back pattern: ${nBackPattern}`
    );
    console.log(`  Is correct match: ${isCorrectMatch}, User said: ${isMatch}`);

    if ((isMatch && isCorrectMatch) || (!isMatch && !isCorrectMatch)) {
      // Correct answer
      console.log(`  Correct response!`);
      setScore((prev) => prev + 10);
      setCorrectResponses((prev) => prev + 1);
      setFeedbackCorrect(true);
    } else {
      // Incorrect answer
      console.log(`  Incorrect response!`);
      setScore((prev) => Math.max(0, prev - 5));
      setIncorrectResponses((prev) => prev + 1);
      setFeedbackCorrect(false);
    }

    // Show feedback
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 500);

    // Update game history using functional update to ensure we have the latest state
    setGameHistory((prevHistory) => {
      const newHistory = [...prevHistory];
      newHistory[currentPosition] = isMatch;
      return newHistory;
    });
  };

  // Handle the tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 4) {
      setTutorialStep((prev) => prev + 1);

      // If moving to step 1, start showing a pattern
      // If moving to step 1, start showing a pattern
      if (tutorialStep === 0) {
        const pattern = [4, 1, 4, 7]; // Example pattern for tutorial
        setTutorialPatternHistory(pattern);
        showTutorialPattern(pattern, 0);
      }
    } else {
      // End tutorial and start the game
      setGameMode("play");
      startGame();
    }
  };

  // Show tutorial patterns
  const showTutorialPattern = (patterns, index) => {
    if (index >= patterns.length) {
      setTutorialShowPattern(false);
      return;
    }

    setTutorialShowPattern(true);
    setActiveSquare(patterns[index]);

    setTimeout(() => {
      setTutorialShowPattern(false);
      setActiveSquare(null);

      setTimeout(() => {
        showTutorialPattern(patterns, index + 1);
      }, 800);
    }, 1000);
  };

  useEffect(() => {
    console.log("Current position updated:", currentPosition);
  }, [currentPosition]);

  useEffect(() => {
    console.log("Active square updated:", activeSquare);
  }, [activeSquare]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-blue-300">
              Memory Matrix
            </h1>
            <div className="p-2 bg-blue-500/10 rounded-full">
              <Brain className="text-blue-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Brain className="text-blue-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Memory Matrix Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      Game Overview
                    </h3>
                    <p className="text-slate-300">
                      Memory Matrix is a classic N-back task that challenges
                      your working memory. You'll see a sequence of patterns on
                      a grid and need to identify when the current pattern
                      matches what you saw "n" positions back in the sequence.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      How to Play
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-300">
                      <li>
                        Watch as squares light up one at a time in a 3×3 grid
                      </li>
                      <li>Remember the position of each highlighted square</li>
                      <li>
                        If the current position matches the position from n
                        steps back, press "Match"
                      </li>
                      <li>
                        If the current position is different from n steps back,
                        press "No Match"
                      </li>
                      <li>
                        Your score increases for correct answers and decreases
                        for incorrect ones
                      </li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      Difficulty Levels
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        <span className="font-medium">1-Back:</span> Compare
                        with the immediately previous position
                      </li>
                      <li>
                        <span className="font-medium">2-Back:</span> Compare
                        with the position 2 steps back
                      </li>
                      <li>
                        <span className="font-medium">3-Back:</span> Compare
                        with the position 3 steps back
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      Recovery Benefits
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        Strengthens working memory capacity impaired by
                        substance use
                      </li>
                      <li>
                        May lead to improvements in tasks related to working
                        memory, which some studies suggest can transfer to
                        aspects of reasoning or fluid intelligence, though
                        findings are mixed.
                      </li>
                      <li>
                        May improve attentional control and focus, as shown by
                        some cognitive training studies
                      </li>
                      <li>
                        Helps rebuild neural pathways in the prefrontal cortex
                      </li>
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-cyan-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Memory Matrix
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Challenge your working memory with this N-back task. Remember
                  positions and identify patterns to help strengthen your brain!
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Working Memory
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Trains your ability to hold and manipulate information
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">10-15 minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Recommended session length for training
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Track your progress and compete with others
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        onClick={() => setLevel(n)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          level === n
                            ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {n}-Back
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((score, index) => {
                          // Format the name to show first name and first letter of last name
                          let formattedName = score.userName;

                          if (score.userName && score.userName.includes(" ")) {
                            const nameParts = score.userName.split(" ");
                            const firstName = nameParts[0];
                            const lastInitial = nameParts[1]
                              ? nameParts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }

                          return (
                            <tr
                              key={score.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300">
                                {score.level}-Back
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.averageReactionTime
                                  ? Math.round(score.averageReactionTime) +
                                    " ms"
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-right text-blue-300 font-medium">
                                {score.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: {level}-Back Task
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        In this game, you'll see squares light up one by one on
                        a 3×3 grid. Your goal is to identify when the current
                        square position matches the one from {level} steps back
                        in the sequence.
                      </p>
                      <p className="text-slate-300">
                        Let's walk through an example together. I'll show you a
                        sequence of patterns and guide you through how to
                        respond.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Watch this sequence of patterns carefully. We're playing
                        a {level}-back task, so you'll compare each pattern with
                        the one that appeared {level} positions earlier.
                      </p>
                      <p className="text-slate-300">
                        For the first {level} patterns, you don't need to
                        respond since there's no previous pattern to compare
                        with. Just observe and remember them.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Great! Now let's analyze what we saw:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-slate-300">
                        <li>
                          The first pattern appeared in the center (position 4)
                        </li>
                        <li>
                          The second pattern appeared in the top-middle
                          (position 1)
                        </li>
                        <li>
                          The third pattern appeared in the center again
                          (position 4)
                        </li>
                        <li>
                          The fourth pattern appeared in the bottom-middle
                          (position 7)
                        </li>
                      </ul>
                      <p className="text-slate-300">
                        Let's see how we would respond in a {level}-back task...
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        In a {level}-back task, we compare the current position
                        with the one from {level} steps back:
                      </p>

                      {level === 1 && (
                        <ul className="list-disc list-inside space-y-2 text-slate-300">
                          <li>
                            For position 1 (center): No response needed (first
                            position)
                          </li>
                          <li>
                            For position 2 (top-middle): No match with position
                            1 → Press "No Match"
                          </li>
                          <li>
                            For position 3 (center): Matches position 1 → Press
                            "Match"
                          </li>
                          <li>
                            For position 4 (bottom-middle): No match with
                            position 2 → Press "No Match"
                          </li>
                        </ul>
                      )}

                      {level === 2 && (
                        <ul className="list-disc list-inside space-y-2 text-slate-300">
                          <li>
                            For position 1 (center): No response (first
                            position)
                          </li>
                          <li>
                            For position 2 (top-middle): No response (second
                            position)
                          </li>
                          <li>
                            For position 3 (center): No match with position 1 →
                            Press "No Match"
                          </li>
                          <li>
                            For position 4 (bottom-middle): No match with
                            position 2 → Press "No Match"
                          </li>
                        </ul>
                      )}

                      {level === 3 && (
                        <ul className="list-disc list-inside space-y-2 text-slate-300">
                          <li>
                            For position 1 (center): No response (first
                            position)
                          </li>
                          <li>
                            For position 2 (top-middle): No response (second
                            position)
                          </li>
                          <li>
                            For position 3 (center): No response (third
                            position)
                          </li>
                          <li>
                            For position 4 (bottom-middle): Matches position 1 →
                            Press "Match"
                          </li>
                        </ul>
                      )}
                    </div>
                  )}

                  {tutorialStep === 4 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        You've got it! Here's a quick summary of how to play:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-slate-300">
                        <li>
                          Watch each pattern carefully and remember the sequence
                        </li>
                        <li>
                          Compare the current pattern with the one from {level}{" "}
                          steps back
                        </li>
                        <li>
                          Press "Match" if they're the same, "No Match" if
                          they're different
                        </li>
                        <li>
                          The first {level} patterns don't need a response
                        </li>
                        <li>
                          You gain points for correct answers and lose points
                          for incorrect ones
                        </li>
                      </ul>
                      <p className="text-slate-300">
                        Ready to start playing? Let's go!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tutorial visualization */}
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="flex-1 flex justify-center">
                  <div
                    className="grid grid-cols-3 gap-2 bg-slate-900/60 p-4 rounded-xl border border-slate-700"
                    ref={gridRef}
                  >
                    {[...Array(numSquares)].map((_, index) => (
                      <div
                        key={index}
                        className={`w-16 h-16 md:w-20 md:h-20 rounded-lg border-2 transition-colors ${
                          tutorialShowPattern && activeSquare === index
                            ? `${primaryColorSolid} border-blue-400`
                            : "bg-slate-800/60 border-slate-700 hover:border-slate-600"
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  {tutorialStep > 0 && (
                    <div className="bg-slate-700/30 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-2">
                        Pattern Sequence:
                      </h3>
                      <div className="flex items-center gap-3 overflow-x-auto pb-2">
                        {tutorialPatternHistory.map((position, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col items-center shrink-0"
                          >
                            <div
                              className={`w-10 h-10 rounded-lg ${primaryColorSolid} flex items-center justify-center`}
                            >
                              {position + 1}
                            </div>
                            <span className="text-xs text-slate-400 mt-1">
                              Pos {idx + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex justify-between">
                    {tutorialStep > 0 ? (
                      <button
                        onClick={() => setTutorialStep((prev) => prev - 1)}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <button
                        onClick={() => setGameMode("intro")}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={advanceTutorial}
                      className="py-2 px-4 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors"
                    >
                      {tutorialStep < 4 ? "Next" : "Start Game"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game Play Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                  <span className="text-blue-300 font-medium">
                    Level: {level}-Back
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Game grid */}
                <div className="relative">
                  <div
                    className="grid grid-cols-3 gap-3 bg-slate-900/60 p-5 rounded-xl border border-slate-700"
                    ref={gridRef}
                  >
                    {[...Array(numSquares)].map((_, index) => (
                      <div
                        key={index}
                        className={`w-20 h-20 md:w-24 md:h-24 rounded-lg border-2 transition-colors ${
                          activeSquare === index
                            ? `${primaryColorSolid} border-blue-400`
                            : "bg-slate-800/60 border-slate-700"
                        }`}
                      ></div>
                    ))}
                  </div>

                  {/* Feedback overlay */}
                  <AnimatePresence>
                    {showFeedback && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div
                          className={`p-6 rounded-full ${
                            feedbackCorrect
                              ? "bg-green-500/20 text-green-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {feedbackCorrect ? (
                            <CheckCircle size={60} />
                          ) : (
                            <XCircle size={60} />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Game controls */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Game Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">Position</span>
                      <span className="text-white font-medium">
                        {currentPosition + 1}/{currentSequence.length}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                        style={{
                          width: `${
                            (currentPosition / currentSequence.length) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Only show response buttons once we've passed the first n patterns */}
                    {currentPosition >= level && (
                      <>
                        <button
                          onClick={() => handleUserInput(true)}
                          className="w-full py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle size={20} />
                          Match
                        </button>

                        <button
                          onClick={() => handleUserInput(false)}
                          className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <XCircle size={20} />
                          No Match
                        </button>
                      </>
                    )}

                    {currentPosition < level && (
                      <div className="w-full py-4 bg-slate-700/30 text-slate-400 rounded-lg text-center">
                        <p>Remember these patterns...</p>
                        <p className="text-xs mt-1">
                          Response buttons will appear after position {level}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed the {level}-Back Memory Matrix challenge.
                </p>
              </div>

              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="text-green-400 font-medium">
                    Correct Responses
                  </span>
                  : When you correctly identified a match or non-match.
                  <br />
                  <span className="text-red-400 font-medium">
                    Incorrect Responses
                  </span>
                  : When you pressed the wrong button.
                  <br />
                  <span className="text-amber-400 font-medium">
                    Missed Matches
                  </span>
                  : When there was a match but you didn't press any button.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-blue-400" />
                    Your Results
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-blue-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {level}-Back
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Responses</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Incorrect Responses
                      </span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Missed Matches</span>
                      <span className="text-amber-400">{misses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Reaction Time</span>
                      <span className="text-white">
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-blue-400" />
                    Performance Analysis
                  </h3>

                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Working memory capacity improvement
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Enhanced attention and focus
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Neural pathway strengthening
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoryMatrix;
